/**
 * **전투 기록**: 끝난 판의 다시 보기 기록(`Replay` — 명부와 커맨드 로그)을 브라우저에 남긴다. 출정 화면의 "전투 기록"에서
 * 골라 다시 본다(`ui/records.ts`). 플레이한 사람이 골랐다 — 처음에는 방금 끝난 한 판만 기억에 두어, 결과 화면을 떠나면
 * 볼 수 없었다.
 *
 * 남기는 것: **최근 `RECENT_KEEP`판**과 **전과가 가장 높은 한 판**(`keepRecords`). 판마다 수 KB에서 수십 KB(명부 + 커맨드)
 * 라 다 남기면 저장 한도(브라우저마다 5MB쯤)를 계정 저장과 다투게 된다.
 *
 * 기록은 그 규칙 버전(`REPLAY_VERSION`)에서만 같은 판이 나온다 — 규칙이 바뀌면 같은 커맨드가 다른 판이 된다. 그래서
 * 읽을 때 버전이 다른 기록은 버린다(`parseRecords`). 틀린 판을 "그때 그 판"이라고 보이는 것보다 없는 편이 낫다.
 *
 * 계정 저장(`meta/army.ts`)과 따로 둔다: 기록이 깨지거나 한도에 걸려도 계정은 멀쩡해야 한다.
 */
import { MAX_MOMENTS } from '../game/replay-moments.ts';
import type { Moment, MomentKind } from '../game/replay-moments.ts';
import { CmdType, MAX_SQUADS } from '../sim/commands.ts';
import { MAX_REPLAY_COMMANDS, MAX_REPLAY_TICKS, REPLAY_VERSION } from '../sim/replay.ts';
import type { Replay } from '../sim/replay.ts';
import { FIELD_COLS, FIELD_ROWS, RAID_ROWS } from '../sim/scenario.ts';
import { MAX_DEPTH } from '../sim/troops.ts';
import { MAX_UNITS } from '../sim/units.ts';

const SAVE_KEY = 'ildangbaek.replays.v1';

/** 남기는 최근 판 수. 그 밖에 전과가 가장 높은 판 하나를 더 남긴다. */
export const RECENT_KEEP = 5;

export interface ReplayRecord {
  /** 끝난 때(ms, `Date.now()`). 목록의 차례와 "3분 전" 표시에 쓴다. 화면만 쓴다 — 시뮬레이션은 모른다. */
  readonly at: number;
  readonly replay: Replay;
  /** 병사 uid → 이름. 전사 알림이 부른다(쓰러진 병사는 계정에 남지 않는다). */
  readonly names: readonly (readonly [number, string])[];
  readonly raid: boolean;
  /** 어느 판인가: "깊이 12", "영토전 · 한성", "단신 토벌". */
  readonly title: string;
  readonly won: boolean;
  /** 토벌에서 물러난 판인가(장수가 살아 전장을 떠났다). 이긴 것도 쓰러진 것도 아니다 — 목록이 "물러남"이라고 적는다. */
  readonly fled?: boolean;
  /** 이 판의 전과(`BattleOutcome.battleMerit`). */
  readonly merit: number;
  readonly moments: readonly Moment[];
}

/** 가장 높은 전과의 판. 같으면 나중 판. 이긴 판만 — 진 판의 전과는 사라진 전과다. 없으면 null. */
export function bestRecord(list: readonly ReplayRecord[]): ReplayRecord | null {
  let best: ReplayRecord | null = null;
  for (const r of list) {
    if (!r.won || r.merit <= 0) continue;
    if (!best || r.merit > best.merit || (r.merit === best.merit && r.at > best.at)) best = r;
  }
  return best;
}

/** 남길 기록: 최근 `RECENT_KEEP`판과 전과가 가장 높은 한 판. 새 판이 앞이다. */
export function keepRecords(list: readonly ReplayRecord[]): ReplayRecord[] {
  const sorted = [...list].sort((a, b) => b.at - a.at);
  const kept = sorted.slice(0, RECENT_KEEP);
  const best = bestRecord(sorted);
  if (best && !kept.includes(best)) kept.push(best);
  return kept;
}

/** 새 판을 더해 남길 것만 추린다. */
export function addRecord(list: readonly ReplayRecord[], rec: ReplayRecord): ReplayRecord[] {
  return keepRecords([...list, rec]);
}

const KINDS: readonly MomentKind[] = ['clash', 'skill', 'mate', 'chief', 'wiped', 'leader'];

function isInt(v: unknown, lo: number, hi: number): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
}

/** 명부의 줄 수와 곁가지 배열(동료·착장·출전 위치 등)의 길이 상한. 실제 판은 병종 수십 줄, 분대 넷이다. */
const MAX_ROSTER_ROWS = 128;
const MAX_SIDE_LIST = 16;
/**
 * 명부를 글로 적은 길이의 상한. 실제 판은 수 KB다(병사 1,400명의 uid를 다 실어도 20KB 안팎). 꼴 검사가 놓친 속의 긴 글을
 * 통째로 막는다 — 착장 칸에 백만 글자를 실은 명부는 꼴이 맞아 지나갔고, 병사마다 그 글을 훑어 판을 세우는 데 0.5초가
 * 걸렸다(재리뷰가 쟀다).
 */
const MAX_CONFIG_CHARS = 100_000;
/** 명부 안의 글(병종·장비 열쇠·길 이름 등) 하나의 길이 상한. 실제 열쇠는 스무 글자 안쪽이다. */
const MAX_KEY_CHARS = 64;

/** 속의 글이 모두 짧은가(깊이 여섯까지 — 그보다 깊은 명부는 없다). */
function shortStrings(v: unknown, depth = 0): boolean {
  if (typeof v === 'string') return v.length <= MAX_KEY_CHARS;
  if (typeof v !== 'object' || v === null) return true;
  if (depth >= 6) return false;
  for (const x of Array.isArray(v) ? v : Object.values(v)) if (!shortStrings(x, depth + 1)) return false;
  return true;
}

/**
 * 저장에서 읽은 명부가 **돌려도 안전한 꼴**인가. 규칙에 맞는 판인지(전과를 속였는지)는 보지 않는다 — 그것은 서버 검증
 * (`verifyReplay`)의 일이고, 여기서 거르는 것은 화면을 붙잡는 입력이다.
 *
 * 처음에는 명부가 객체인지만 보고 `createBattle`에 넘겼다. 판을 세우는 쪽은 저장의 안전한 문이 아니다: 정원 검사
 * (`assertFits`)는 병사 수의 **합**만 보아 `{count: 1e9}`와 `{count: -1e9}`를 실으면 지나가고, 세우는 쪽은 줄마다
 * `count`만큼 돈다 — 작은 글 하나로 화면이 멈췄다(리뷰가 재현했다). 전장의 크기도 그대로 지형 배열이 된다.
 */
export function safeConfig(raw: unknown): boolean {
  if (typeof raw !== 'object' || raw === null) return false;
  const c = raw as Record<string, unknown>;
  if (!isInt(c.seed, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER)) return false;
  // 전장의 크기는 규칙이 정한 것뿐이다(`verifyReplay`와 같다). 다른 크기는 그만큼의 지형을 세운다.
  if (c.cols !== FIELD_COLS || (c.rows !== FIELD_ROWS && c.rows !== RAID_ROWS)) return false;
  if (!isInt(c.depth, 1, MAX_DEPTH)) return false;
  if (!Array.isArray(c.roster) || c.roster.length > MAX_ROSTER_ROWS) return false;
  let total = 0;
  for (const e of c.roster) {
    if (typeof e !== 'object' || e === null) return false;
    const row = e as Record<string, unknown>;
    // 줄마다 0 이상 — 음수로 합을 상쇄하지 못하게 줄 하나하나를 본다.
    if (typeof row.troop !== 'string' || !isInt(row.count, 0, MAX_UNITS)) return false;
    total += row.count;
    if (total > MAX_UNITS) return false;
    if (row.squad !== undefined && !isInt(row.squad, 0, MAX_SQUADS - 1)) return false;
    for (const key of ['uids', 'fatigue'] as const) {
      const list = row[key];
      if (list === undefined) continue;
      if (!Array.isArray(list) || list.length > row.count || !list.every((v) => isInt(v, 0, Number.MAX_SAFE_INTEGER))) return false;
    }
  }
  for (const key of ['companions', 'loadouts', 'companionLevels', 'traits', 'deploy', 'supplies'] as const) {
    const list = c[key];
    if (list !== undefined && (!Array.isArray(list) || list.length > MAX_SIDE_LIST)) return false;
  }
  // 속의 글은 모두 짧고, 통째로 적어도 길지 않다.
  if (!shortStrings(c)) return false;
  try {
    if (JSON.stringify(c).length > MAX_CONFIG_CHARS) return false;
  } catch {
    return false;
  }
  return true;
}

const CMD_TYPES: ReadonlySet<number> = new Set(Object.values(CmdType));

/**
 * 커맨드 배열(넷씩: 틱·갈래·a·b)이 돌려도 안전한 꼴인가: 틱은 1부터 기록의 끝까지 오름차순(`CommandLog.push`는 거꾸로 온
 * 틱에 던진다), 갈래는 아는 것, 인자는 32비트 정수.
 */
export function safeCommands(raw: unknown, ticks: number): raw is number[] {
  if (!Array.isArray(raw) || raw.length > MAX_REPLAY_COMMANDS * 4 || raw.length % 4 !== 0) return false;
  let last = 0;
  for (let i = 0; i < raw.length; i += 4) {
    if (!isInt(raw[i], Math.max(1, last), ticks)) return false;
    last = raw[i];
    if (!CMD_TYPES.has(raw[i + 1])) return false;
    if (!isInt(raw[i + 2], -(2 ** 31), 2 ** 31 - 1) || !isInt(raw[i + 3], -(2 ** 31), 2 ** 31 - 1)) return false;
  }
  return true;
}

/**
 * 화면에 적을 글에서 태그가 될 글자를 걷는다. 장면의 글은 알림(`Hud.say`)에 실리는데 그쪽은 HTML을 받는다 — 저장을 고쳐
 * `<img onerror=…>`를 실으면 그대로 실행됐다(리뷰가 재현했다). 알리는 쪽도 글로만 넣지만(`main.ts`의 `plain`) 문 앞에서도
 * 걷는다. 우리가 적는 글에는 이 글자가 없다.
 */
function cleanText(text: string, max: number): string {
  return text.replace(/[<>&"'`]/g, '').slice(0, max);
}

/**
 * 저장에서 읽은 것 하나를 기록으로. 꼴이 틀리거나 규칙 버전이 다르면 null. 저장은 믿지 않는다 — 손으로 고쳤을 수도, 옛
 * 버전이 남긴 것일 수도 있다. 명부와 커맨드는 돌려도 안전한 꼴인지 본다(`safeConfig`·`safeCommands`). 그래도 판을 세우지
 * 못하는 기록(모르는 병종 등)은 다시 보기를 열 때 걸러진다(`main.ts`의 `openReplay`가 잡는다).
 */
function parseRecord(raw: unknown): ReplayRecord | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const rp = r.replay as Record<string, unknown> | null | undefined;
  if (typeof rp !== 'object' || rp === null) return null;
  if (rp.version !== REPLAY_VERSION) return null;
  if (!isInt(rp.ticks, 1, MAX_REPLAY_TICKS)) return null;
  if (!safeConfig(rp.config)) return null;
  const cmds = rp.commands;
  if (!safeCommands(cmds, rp.ticks)) return null;
  if (!isInt(r.at, 0, Number.MAX_SAFE_INTEGER)) return null;
  if (typeof r.title !== 'string' || typeof r.won !== 'boolean' || typeof r.raid !== 'boolean') return null;
  if (!isInt(r.merit, 0, Number.MAX_SAFE_INTEGER)) return null;
  const names: [number, string][] = [];
  if (Array.isArray(r.names)) {
    // 이름은 판에 나간 병사 수를 넘지 않는다.
    for (const n of r.names.slice(0, MAX_UNITS)) {
      if (Array.isArray(n) && isInt(n[0], 0, Number.MAX_SAFE_INTEGER) && typeof n[1] === 'string') names.push([n[0], cleanText(n[1], 40)]);
    }
  }
  const moments: Moment[] = [];
  if (Array.isArray(r.moments)) {
    for (const m of r.moments.slice(0, MAX_MOMENTS)) {
      if (typeof m !== 'object' || m === null) continue;
      const mm = m as Record<string, unknown>;
      if (!isInt(mm.tick, 0, rp.ticks) || typeof mm.label !== 'string' || !KINDS.includes(mm.kind as MomentKind)) continue;
      moments.push({ tick: mm.tick, kind: mm.kind as MomentKind, label: cleanText(mm.label, 60) });
    }
    moments.sort((a, b) => a.tick - b.tick);
  }
  return {
    at: r.at,
    replay: { version: REPLAY_VERSION, config: rp.config as Replay['config'], ticks: rp.ticks, commands: cmds },
    names,
    raid: r.raid,
    title: cleanText(r.title, 60),
    won: r.won,
    ...(r.fled === true && !r.won ? { fled: true } : {}),
    merit: r.merit,
    moments,
  };
}

/** 저장한 글(JSON)에서 기록을 읽는다. 틀린 것·옛 버전은 빼고, 남길 것만 추려 돌려준다. */
export function parseRecords(text: string | null): ReplayRecord[] {
  if (!text) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  const out: ReplayRecord[] = [];
  for (const r of raw.slice(0, 50)) {
    const rec = parseRecord(r);
    if (rec) out.push(rec);
  }
  return keepRecords(out);
}

export function loadRecords(): ReplayRecord[] {
  try {
    return parseRecords(localStorage.getItem(SAVE_KEY));
  } catch {
    // 저장을 읽을 수 없다(비공개 창 등). 기록 없이 간다.
    return [];
  }
}

/** 가장 오랜 판 하나를 던다. 최고 전과 판은 끝까지 남긴다 — 그것만 남았으면 그것을 던다. */
export function dropOldest(list: readonly ReplayRecord[]): ReplayRecord[] {
  if (list.length <= 1) return [];
  const best = bestRecord(list);
  let oldest: ReplayRecord | null = null;
  for (const r of list) {
    if (r === best) continue;
    if (!oldest || r.at < oldest.at) oldest = r;
  }
  return list.filter((r) => r !== oldest);
}

/** 저장소의 꼴(시험이 가짜를 넣는다). */
export interface Store {
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * 기록을 저장한다. 한도에 걸리면 가장 오랜 판부터 하나씩 덜어 다시 해 본다 — 큰 판 하나 때문에 새 판을 못 남기는 것보다
 * 옛 판을 잃는 편이 낫다. 실제로 저장한 목록을 돌려준다(하나도 못 남겼으면 빈 목록 — 기억에는 부르는 쪽이 들고 있다).
 */
export function saveRecords(list: readonly ReplayRecord[], store?: Store): ReplayRecord[] {
  let keep = keepRecords(list);
  for (;;) {
    try {
      const s = store ?? localStorage;
      if (keep.length === 0) s.removeItem(SAVE_KEY);
      else s.setItem(SAVE_KEY, JSON.stringify(keep));
      return keep;
    } catch {
      if (keep.length === 0) return [];
      keep = dropOldest(keep);
    }
  }
}

/**
 * **계정을 먼저, 기록은 남는 자리에.** 계정과 기록은 같은 저장 한도를 나눠 쓴다. 계정 저장(`saveAccount` — 성공하면 true)이
 * 한도에 걸리면 기록을 오랜 판부터 덜어 내며 다시 해 본다. 실제로 남긴 기록을 돌려준다.
 *
 * 처음에는 기록을 먼저 저장하고 계정을 나중에 저장했다. 기록이 자리를 먼저 차지하면, 정산으로 커진 계정(합류·전리품)이
 * 한도에 걸려 **조용히 저장되지 않았다** — 다시 열면 그 판의 정산이 사라진다(리뷰가 가짜 한도로 재현했다). 기록은 잃어도
 * 되는 것이고 계정은 아니다.
 */
export function saveAccountThenRecords(
  saveAccount: () => boolean,
  list: readonly ReplayRecord[],
  store?: Store,
): ReplayRecord[] {
  let keep = keepRecords(list);
  // 지금 있는 기록을 둔 채 계정이 들어가면 그대로 간다. 안 들어가면 기록을 덜어 자리를 낸다.
  while (!saveAccount()) {
    if (keep.length === 0) {
      // 남길 기록이 없는데도 계정이 안 들어간다. 저장소에는 아직 기록이 차 있을 수 있다 — 규칙이 바뀐 업데이트 뒤에는
      // 옛 기록이 읽을 때 모두 버려져 기억의 목록은 비었는데 저장 키는 그대로다. 그 키를 지우고 계정을 **한 번 더**
      // 넣어 본다. 처음에는 지우기만 하고 돌아가, 자리가 났는데도 계정이 저장되지 않았다(재리뷰가 재현했다).
      // 그래도 안 들어가면 저장을 쓸 수 없는 창이거나 계정만으로 한도다 — 여기서 끝낸다(끝없이 돌지 않는다).
      saveRecords([], store);
      saveAccount();
      return [];
    }
    keep = dropOldest(keep);
    saveRecords(keep, store);
  }
  // 계정이 들어갔다. 남은 자리에 기록을 넣는다 — 넘치면 `saveRecords`가 덜어 낸다. 계정은 이미 들어가 있다.
  return saveRecords(keep, store);
}

/** 기록을 모두 지운다(계정을 처음부터 다시 시작할 때). */
export function clearRecords(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    // 저장을 쓸 수 없으면 지울 것도 없다.
  }
}
